export class GroupActionError extends Error {
    constructor(action, failed, total) {
        super(`${failed} of ${total} group ${action} calls failed`);
        this.name = 'GroupActionError';
        this.action = action;
        this.failed = failed;
        this.total = total;
    }
}
export function describeGroupFailure(action, err) {
    if (err instanceof GroupActionError) {
        const noun = err.total === 1 ? 'item' : 'items';
        return `Couldn't ${action} ${err.failed} of ${err.total} ${noun}. Try again.`;
    }
    return `Couldn't ${action} the selected items. Try again.`;
}
// Display order of the given ids: folder list order, then row order inside
// each folder. Unknown ids are dropped, duplicates collapse.
export function orderArtifactIds(folders, ids) {
    const wanted = new Set(ids);
    const out = [];
    for (const f of folders) {
        for (const a of f.artifacts) {
            if (wanted.has(a.id))
                out.push(a.id);
        }
    }
    return out;
}
export function artifactsToMove(folders, ids, targetFolderId) {
    var _a, _b;
    const inTarget = new Set((_b = (_a = folders.find(f => f.id === targetFolderId)) === null || _a === void 0 ? void 0 : _a.artifacts.map(a => a.id)) !== null && _b !== void 0 ? _b : []);
    return orderArtifactIds(folders, ids).filter(id => !inTarget.has(id));
}
// Pull the ids out of whichever folders hold them and append them to the
// target in the order given. Ids already in the target stay where they are.
export function moveArtifactsToFolder(folders, orderedIds, targetFolderId) {
    const target = folders.find(f => f.id === targetFolderId);
    if (!target)
        return folders;
    const alreadyThere = new Set(target.artifacts.map(a => a.id));
    const moving = new Set(orderedIds.filter(id => !alreadyThere.has(id)));
    if (moving.size === 0)
        return folders;
    const pulled = new Map();
    const stripped = folders.map(f => {
        if (f.id === targetFolderId || !f.artifacts.some(a => moving.has(a.id)))
            return f;
        const kept = [];
        for (const a of f.artifacts) {
            if (moving.has(a.id))
                pulled.set(a.id, { ...a, folderId: targetFolderId });
            else
                kept.push(a);
        }
        return { ...f, artifacts: kept };
    });
    if (pulled.size === 0)
        return folders;
    const appended = [];
    for (const id of orderedIds) {
        const a = pulled.get(id);
        if (a) {
            appended.push(a);
            pulled.delete(id);
        }
    }
    return stripped.map(f => (f.id === targetFolderId ? { ...f, artifacts: [...f.artifacts, ...appended] } : f));
}
// Put the ids back where the snapshot had them. Used when some calls of a
// group move were rejected, so the panel never shows a move that did not
// happen even when the follow-up refetch also fails.
export function restoreArtifacts(current, snapshot, ids) {
    const idSet = new Set(ids);
    if (idSet.size === 0)
        return current;
    const placements = new Map();
    for (const f of snapshot) {
        f.artifacts.forEach((artifact, index) => {
            var _a;
            if (!idSet.has(artifact.id))
                return;
            const bucket = (_a = placements.get(f.id)) !== null && _a !== void 0 ? _a : [];
            bucket.push({ index, artifact });
            placements.set(f.id, bucket);
        });
    }
    return current.map(f => {
        const removed = f.artifacts.filter(a => !idSet.has(a.id));
        const inserts = placements.get(f.id);
        if (!inserts)
            return removed.length === f.artifacts.length ? f : { ...f, artifacts: removed };
        const next = [...removed];
        for (const { index, artifact } of inserts) {
            next.splice(Math.min(index, next.length), 0, artifact);
        }
        return { ...f, artifacts: next };
    });
}
// Inclusive run of ids between the anchor and the target within one folder's
// ordered rows. Null when either end is not in that folder, which the panel
// treats as a toggle.
export function rangeWithinFolder(orderedIds, anchorId, targetId) {
    if (!anchorId)
        return null;
    const from = orderedIds.indexOf(anchorId);
    const to = orderedIds.indexOf(targetId);
    if (from < 0 || to < 0)
        return null;
    return orderedIds.slice(Math.min(from, to), Math.max(from, to) + 1);
}
export function toggleSelection(selected, id) {
    const next = new Set(selected);
    if (next.has(id))
        next.delete(id);
    else
        next.add(id);
    return next;
}
export function addToSelection(selected, ids) {
    const next = new Set(selected);
    for (const id of ids)
        next.add(id);
    return next;
}
// Drops ids that no longer exist in any folder. Returns the same set when
// nothing changed so a state update is a no-op.
export function pruneSelection(selected, folders) {
    if (selected.size === 0)
        return selected;
    const present = new Set();
    for (const f of folders) {
        for (const a of f.artifacts) {
            if (selected.has(a.id))
                present.add(a.id);
        }
    }
    return present.size === selected.size ? selected : present;
}
